import emailRegex from 'common/services/regex/email-regex';
import { NestedValidationObj, ValidationObj } from 'common/types/ValidationObj';
import { ApplicationStep } from 'domain/enums/ApplicationStep';
import DwellingTypes from 'domain/enums/DwellingTypes';
import DraftApplication from 'domain/types/DraftApplication';

export function isValid(step: ApplicationStep, app: DraftApplication) {
  const errors = getErrorsForStep(step, app);
  if (step === ApplicationStep.address) {
    if (
      errors.meta !== undefined ||
      Object.values(errors.address as ValidationObj).some((v) => v != undefined)
    ) {
      return false;
    }
  } else if (Object.values(errors).some((v) => v != undefined)) {
    // If there are any errors, prevent navigation
    return false;
  }
  return true;
}

export default function getErrorsForStep(
  step: ApplicationStep,
  draft: DraftApplication
): ValidationObj {
  switch (step) {
    case ApplicationStep.contact:
      return validateContact(draft);
    case ApplicationStep.address:
      return validateAddress(draft);
    case ApplicationStep.relationship:
      return validateRelationship(draft);
    case ApplicationStep.details:
      return validateDetails(draft);
    default: {
      return {};
    }
  }
}

export function getErrors(draft: DraftApplication) {
  return {
    ...validateContact(draft),
    ...validateAddress(draft),
    ...validateRelationship(draft),
    ...validateDetails(draft),
  };
}

export function validateContact(draft: DraftApplication) {
  const errors: ValidationObj = {
    email: undefined,
  };
  if (!draft.email) {
    errors.email = 'You must provide an email address';
  } else if (!emailRegex.test(draft.email)) {
    errors.email =
      'Your email address must be in the format of email@domain.com.';
  }
  return errors;
}
export function validateAddress(draft: DraftApplication) {
  const address: NestedValidationObj = {
    houseNumber: undefined,
    halfNumber: undefined,
    streetDir: undefined,
    streetName: undefined,
    streetType: undefined,
    unitNumber: undefined,
    city: undefined,
    state: undefined,
    zipCode: undefined,
    country: undefined,
    createdBy: undefined,
    createdDate: undefined,
    modifiedBy: undefined,
    modifiedDate: undefined,
    unitType: undefined,
    singleFamilyHome: undefined,
  };

  const zipRegex = /^[\d]{5}$/;

  const errors: ValidationObj = {
    meta: undefined,
    address,
  };
  if (!draft.address) {
    errors.meta = 'Please select or enter an address.';
    return errors as ValidationObj;
  }

  if (draft.address.houseNumber) {
    if (
      isNaN(parseInt(draft.address.houseNumber)) ||
      parseInt(draft.address.houseNumber) < 0
    ) {
      address.houseNumber = 'House number must be a positive number.';
    }
    // In case user tries to use scientific notation...
    if (draft.address.houseNumber.includes('e')) {
      address.houseNumber = 'House number must be a valid number.';
    }
  } else {
    address.houseNumber = 'You must provide a house number.';
  }
  if (!draft.address.streetName) {
    address.streetName = 'You must provide street name.';
  }
  if (draft.address.singleFamilyHome !== 'Y') {
    if (!draft.address.unitType) {
      address.unitType = 'You must provide unit type.';
    }
    if (!draft.address.unitNumber) {
      address.unitNumber = 'You must provide unit number.';
    }
  }
  if (draft.address.zipCode) {
    if (!zipRegex.test(draft.address.zipCode)) {
      address.zipCode = 'Zip must be 5 digits long.';
    }
  } else {
    address.zipCode = 'You must provide zip.';
  }

  errors.address = address;
  return errors as ValidationObj;
}

export function validateRelationship(draft: DraftApplication): ValidationObj {
  const errors: ValidationObj = {
    propertyRelaCd: undefined,
    numberOfRooms: undefined,
    primaryResIndicator: undefined,
    residenceAttachment: undefined,
  };
  if (!draft.propertyRelaCd || draft.propertyRelaCd === DwellingTypes.NONE) {
    errors.propertyRelaCd = 'You must provide a relationship.';
  }
  if (!draft.primaryResIndicator) {
    errors.primaryResIndicator = 'You must answer this question.';
  }
  if (draft.primaryResIndicator === 'Y') {
    if (!draft.hasResidenceAttachment) {
      errors.residenceAttachment = 'You must upload proofs of residence.';
    }
    if (
      draft.hasDifferentAttachmentTypes &&
      !draft.hasDifferentAttachmentTypes
    ) {
      errors.residenceAttachment = 'You must select different document types.';
    }
  }

  if (!draft.numberOfRooms) {
    errors.numberOfRooms = 'You must enter the number of sleeping rooms.';
  } else if (draft.numberOfRooms <= 0) {
    errors.numberOfRooms = 'Please enter a number greater than zero.';
  }

  return errors;
}

export function validateDetails(draft: DraftApplication): ValidationObj {
  const errors: ValidationObj = {
    dwellingTypeCd: undefined,
    intermediaries: undefined,
    howManyRooms: undefined,
  };

  if (!draft.dwellingTypeCd || draft.dwellingTypeCd === DwellingTypes.NONE) {
    errors.dwellingTypeCd = 'You must enter the dwelling type.';
  }
  if (
    !draft.intermediaries ||
    (draft.intermediaries &&
      draft.intermediaries.filter((i) => Boolean(i)).length === 0)
  ) {
    errors.intermediaries = ['You must select at least one intermediary.'];
  }

  if (
    draft.dwellingTypeCd === DwellingTypes.PARTIAL ||
    draft.dwellingTypeCd === DwellingTypes.COMBINATION
  ) {
    if (!draft.howManyRooms) {
      errors.howManyRooms =
        'You must enter the number of rooms you are listing.';
    }
  }

  return errors;
}
